// ============================================================================
// PLATFORM OPS COMMAND CENTER — Platform Admin wrapper
// Adds tenant filter on top of OpsManagerDashboardView.
// "All Tenants" hides Live Fires; selecting a tenant shows Live Fires.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Building } from 'lucide-react';
import { getTenants } from '../core/index.js';
import OpsManagerDashboardView from './Analytics/OpsManagerDashboardView.jsx';

export default function PlatformOpsCommandCenter() {
  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('all');
  const [loadingTenants, setLoadingTenants] = useState(true);

  useEffect(() => {
    getTenants()
      .then((t) => {
        setTenants(t);
        const ids = Object.keys(t);
        if (ids.length === 1) setSelectedTenantId(ids[0]);
      })
      .catch(() => {})
      .finally(() => setLoadingTenants(false));
  }, []);

  const sortedTenants = Object.entries(tenants)
    .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''));

  const isAllTenants = selectedTenantId === 'all';

  if (loadingTenants) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (sortedTenants.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="rounded-2xl border border-slate-700/40 bg-slate-900/40 p-10 text-center">
          <Building className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <p className="text-sm text-slate-400">No tenants found. Create a tenant first.</p>
        </div>
      </div>
    );
  }

  const tenantFilter = (
    <div className="flex items-center gap-3">
      <Building className="w-4 h-4 text-slate-500" />
      <select
        value={selectedTenantId}
        onChange={(e) => setSelectedTenantId(e.target.value)}
        className="h-9 px-3 rounded-xl bg-slate-800/60 border border-slate-700/40 text-sm text-slate-200 hover:bg-slate-700/60 transition-all focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
      >
        <option value="all">All Tenants (Aggregated)</option>
        {sortedTenants.map(([id, t]) => (
          <option key={id} value={id}>{t.name || id}</option>
        ))}
      </select>
      {!isAllTenants && (
        <span className="text-xs text-slate-500">
          Viewing: <span className="text-slate-300">{tenants[selectedTenantId]?.name || selectedTenantId}</span>
        </span>
      )}
    </div>
  );

  if (isAllTenants) {
    return (
      <OpsManagerDashboardView
        key="all-tenants"
        allTenantIds={Object.keys(tenants)}
        hideLiveFires={true}
        headerSlot={tenantFilter}
      />
    );
  }

  return (
    <OpsManagerDashboardView
      key={selectedTenantId}
      tenantIdOverride={selectedTenantId}
      hideLiveFires={false}
      headerSlot={tenantFilter}
    />
  );
}
